import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { creditsLeft, useAiCredits } from '../../utils/aiCredits';
import AiCreditsDialog from './AiCreditsDialog';

/*
 * How many AI identifications a metered account has left (utils/aiCredits.js),
 * opening its credits dialog. Nothing at all for an account that is not
 * metered. `as` draws it as a host's own icon button (Layout's header, like
 * components/chat/ChatButton.js); otherwise a pill, `tone` 'dark' over the
 * Field camera's picture, 'light' on a page (the survey).
 */
export default function AiCreditsBadge({ user, as: Host, className, tone = 'light', testId = 'ai-credits-badge' }) {
  const { t } = useTranslation('credits');
  const { metered, credits } = useAiCredits(user);
  const [open, setOpen] = useState(false);
  if (!metered) return null;

  const left = credits ? creditsLeft(credits) : null;
  const label = left === null ? t('badge.loading') : t('badge.label', { count: left });
  const content = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span aria-hidden="true">✨</span>
      <span data-testid={`${testId}-count`}>{left === null ? '…' : left}</span>
    </span>
  );
  return (
    <>
      {Host ? (
        <Host className={className} label={label} data-testid={testId} aria-haspopup="dialog"
          onClick={() => setOpen(true)}>
          {content}
        </Host>
      ) : (
        <button type="button" style={{ ...pill, ...(tone === 'dark' ? dark : light) }} aria-label={label}
          title={t('tip.badge')} aria-haspopup="dialog" data-testid={testId} onClick={() => setOpen(true)}>
          {content}
        </button>
      )}
      {open && <AiCreditsDialog credits={credits} onClose={() => setOpen(false)} />}
    </>
  );
}

const pill = {
  display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 14px', borderRadius: 999,
  font: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
};
// Over a live picture: white on dark, as the camera's other chips.
const dark = { color: 'var(--c-on-accent)', background: 'rgba(0, 0, 0, 0.72)', border: '1px solid transparent' };
const light = { color: 'var(--c-text)', background: 'var(--c-surface)', border: '1px solid var(--c-border-strong)' };
